import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Seo from "@/components/base/Seo";
import ApiState from "@/components/base/ApiState";
import { getPlans, getSiteStats } from "@/services/publicApi";
import { useApiResource } from "@/hooks/useApiResource";
import { plansHowItWorks } from "@/mocks/plansPage";
import { OG_IMAGES } from "@/config/ogImages";
import type { Plan } from "@/types";
import PlansHero from "./components/PlansHero";
import PlanCard from "./components/PlanCard";
import CompareTable from "./components/CompareTable";
import PlanFaq from "./components/PlanFaq";
import CtaBand from "./components/CtaBand";

const subscribe = (p: Plan) => p.group !== "placement";
const isPlacement = (p: Plan) => p.group === "placement";

export default function ListingPlans() {
  const { data, loading, error, reload } = useApiResource(() => getPlans(), []);
  const plans = data ?? [];
  const { data: statsData } = useApiResource(() => getSiteStats(), []);
  const stats = statsData ?? [];
  const [open, setOpen] = useState<string | null>(null);

  const formatPrice = useMemo(
    () => (price: number) => (price === 0 ? "Free" : `₹${price.toLocaleString("en-IN")}`),
    [],
  );

  const subscriptionPlans = useMemo(() => plans.filter(subscribe), [plans]);
  const placementPlans = useMemo(() => plans.filter(isPlacement), [plans]);

  return (
    <>
      <Seo
        title="Listing Plans & Pricing"
        description="Compare free, Business and Premium listing plans at Mysore Yellow Pages. Verified badges, featured placement and lead tools for local businesses in Mysore, Mandya, Chamarajanagar and Kodagu."
        keywords="business listing plans, directory pricing, featured listing Mysore, verified badge, sponsored listing"
        path="/listing-plans"
        image={OG_IMAGES.listingPlans}
      />

      <PlansHero />

      <section className="mx-auto w-full max-w-[1400px] px-4 py-14 md:px-6 md:py-16">
        <ApiState
          loading={loading}
          error={error}
          onRetry={reload}
          isEmpty={!loading && !error && plans.length === 0}
          empty={
            <div className="rounded-lg border border-dashed border-background-300 bg-background-50 px-6 py-16 text-center text-sm text-foreground-600">
              Plans are being updated. Please check back shortly.
            </div>
          }
          skeleton={
            <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="h-[520px] animate-pulse rounded-lg border border-background-200 bg-background-100/60"></div>
              ))}
            </div>
          }
        >
          <div className="grid grid-cols-1 gap-6 pt-3 md:grid-cols-3">
            {subscriptionPlans.map((p) => (
              <PlanCard key={p.id} plan={p} formatPrice={formatPrice} />
            ))}
          </div>

          {placementPlans.length > 0 && (
            <div className="mt-14">
              <div className="flex flex-col items-center text-center">
                <span className="inline-flex items-center gap-2 whitespace-nowrap rounded-full bg-accent-100 px-4 py-1.5 text-xs font-bold uppercase tracking-wider text-accent-900">
                  <i className="ri-rocket-2-line text-sm"></i> Boost your visibility
                </span>
                <h2 className="mt-4 font-heading text-2xl font-extrabold text-foreground-950 md:text-3xl">
                  Short-term placement plans
                </h2>
                <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600 md:text-base">
                  Renewable boosts that put your listing in front of more customers — on the homepage and across
                  your district, taluk and chosen category pages.
                </p>
              </div>
              <div className="mt-8 grid grid-cols-1 gap-6 md:grid-cols-2">
                {placementPlans.map((p) => (
                  <PlanCard key={p.id} plan={p} formatPrice={formatPrice} />
                ))}
              </div>
            </div>
          )}

          <div className="mt-10 flex flex-col items-start justify-between gap-5 rounded-lg border border-secondary-200 bg-secondary-50 p-6 md:flex-row md:items-center md:p-8">
            <div className="flex items-start gap-4">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md bg-secondary-500 text-background-50 dark:text-foreground-950">
                <i className="ri-building-4-line text-xl"></i>
              </span>
              <div>
                <h2 className="font-heading text-base font-bold text-foreground-950">
                  Chains, franchises &amp; multi-location brands
                </h2>
                <p className="mt-1 max-w-2xl text-sm leading-relaxed text-foreground-600">
                  Manage several branches under one dashboard with a custom plan built around your footprint across
                  the Mysore region.
                </p>
              </div>
            </div>
            <Link
              to="/contact"
              className="inline-flex shrink-0 items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-3 text-sm font-semibold text-background-50 dark:text-foreground-950 transition-colors hover:bg-primary-600 cursor-pointer"
            >
              Talk to our team
              <i className="ri-arrow-right-line text-base"></i>
            </Link>
          </div>
        </ApiState>
      </section>

      <CtaBand stats={stats} />

      {plans.length > 0 && (
        <section className="border-y border-background-200 bg-background-100">
          <div className="mx-auto w-full max-w-[1400px] px-4 py-14 md:px-6 md:py-16">
            <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
              <div className="max-w-2xl">
                <span className="mb-2 inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-secondary-700">
                  <span className="h-1 w-1 rounded-full bg-accent-500"></span> Compare plans
                </span>
                <h2 className="font-heading text-2xl font-bold leading-tight text-foreground-950 md:text-3xl">
                  Everything included, side by side
                </h2>
                <p className="mt-3 text-sm leading-relaxed text-foreground-600 md:text-base">
                  A transparent breakdown of what each plan gives your business — from featured images and gallery
                  limits to lead tools and SEO.
                </p>
              </div>
            </div>
            <div className="mt-8">
              <CompareTable plans={subscriptionPlans} />
            </div>
          </div>
        </section>
      )}

      <section className="mx-auto w-full max-w-[1400px] px-4 py-14 md:px-6 md:py-16">
        <div className="rounded-lg border border-background-200 bg-background-50 p-8 md:p-10">
          <div className="flex flex-col items-start justify-between gap-4 md:flex-row md:items-end">
            <div className="max-w-xl">
              <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-secondary-700">
                <span className="h-1 w-1 rounded-full bg-accent-500"></span> How it works
              </span>
              <h2 className="mt-2 font-heading text-xl font-bold text-foreground-950 md:text-2xl">
                Three steps to the right local business
              </h2>
            </div>
          </div>
          <div className="mt-8 grid grid-cols-1 gap-6 md:grid-cols-3">
            {plansHowItWorks.map((s) => (
              <div key={s.step} className="relative flex gap-4">
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md bg-primary-500 text-background-50 dark:text-foreground-950">
                  <i className={`${s.icon} text-xl`}></i>
                </span>
                <div>
                  <span className="text-xs font-bold uppercase tracking-wider text-accent-600">{s.step}</span>
                  <h3 className="mt-1 font-heading text-base font-bold text-foreground-950">{s.title}</h3>
                  <p className="mt-1.5 text-sm leading-relaxed text-foreground-600">{s.text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="border-y border-background-200 bg-background-100">
        <div className="mx-auto w-full max-w-[1400px] px-4 py-14 md:px-6 md:py-16">
          <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
            <div className="max-w-2xl">
              <span className="mb-2 inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-secondary-700">
                <span className="h-1 w-1 rounded-full bg-accent-500"></span> Plans FAQ
              </span>
              <h2 className="font-heading text-2xl font-bold leading-tight text-foreground-950 md:text-3xl">
                Questions about plans and billing
              </h2>
              <p className="mt-3 text-sm leading-relaxed text-foreground-600 md:text-base">
                Everything you need to know before choosing a plan — billing, upgrades, expiry and custom options.
              </p>
            </div>
          </div>
          <div className="mt-8">
            <PlanFaq open={open} onToggle={(q) => setOpen(open === q ? null : q)} />
          </div>
        </div>
      </section>

      <section className="bg-primary-900">
        <div className="mx-auto flex w-full max-w-[1400px] flex-col items-center gap-6 px-4 py-14 text-center md:px-6 md:py-16">
          <h2 className="font-heading text-2xl font-extrabold text-background-50 md:text-3xl">
            Ready to get found in Mysore?
          </h2>
          <p className="max-w-2xl text-sm leading-relaxed text-background-300 md:text-base">
            List your business in minutes. Start free, upgrade when your leads start flowing.
          </p>
          <div className="flex flex-col gap-3 sm:flex-row">
            <Link
              to="/list-your-business"
              className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-6 py-3 text-sm font-semibold text-foreground-950 transition-colors hover:bg-accent-600 cursor-pointer"
            >
              <i className="ri-add-circle-line text-base"></i> List your business
            </Link>
            <Link
              to="/directory"
              className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-primary-700 px-6 py-3 text-sm font-semibold text-background-100 transition-colors hover:border-accent-400 hover:text-accent-300 cursor-pointer"
            >
              Browse the directory
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}